/**
 * useAdhocProposalFlow — chat "draft content" intent, decision half.
 *
 * State machine (mirrors useHighlightEditFlow, minus "requesting" —
 * the drafting itself runs server-side and lands via SSE):
 *   idle → pending_decision → applying → idle
 *
 * A proposal arrives from either producer:
 *   - live: the ``adhoc_draft_ready`` SSE event (shell calls ``offer``)
 *   - rehydration: an undecided ``pending_adhoc_proposal`` in the
 *     sections/state snapshot after a pane reload
 * and renders as an Approve/Reject ProposalCard in the thread.
 *
 * Approve is SSOT-FIRST (the mirror image of the legacy Insert path):
 *   1. Mongo — the decision endpoint walks the lifecycle, lands the
 *      sanitized body via the version-checked ``edited`` transition,
 *      and stamps the proposal decided.
 *   2. Word — only after the SSOT commit, ``insertSectionContent``
 *      replaces the section body in-place. A Word failure here leaves
 *      the SSOT correct and the doc stale — surfaced honestly ("use
 *      Load to refresh"), never rolled back.
 *
 * Version drift (writer edited the section after the draft was made)
 * is surfaced as a warning and requires a second, deliberate Approve
 * click armed with the fresh version — never a silent retry, because a
 * full-section replace would clobber those edits.
 */

import { useCallback, useRef, useState } from "react";
import { decideAdhocProposal, getSectionState } from "../../api/client";
import { insertSectionContent } from "../../services/wordWriter";
import type { ApplyStep } from "../components/ApplyProgress";
import { sanitizeDraftForWord } from "../services/draftSanitizer";
import { walkToInserted } from "../services/proposalInsert";
import { submitAgentRedraft } from "../services/writeBackBridge";

// Post-approve apply stages, in execution order for the ad-hoc flow:
// SSOT commit first, then the Word render (the slow part). Reject also
// enters "applying" but does no Word write, so it shows no checklist.
const APPLY_STEP_SAVE = "Saving to the record";
const APPLY_STEP_WORD = "Applying in the Word document";

export interface AdhocProposal {
  runId: string;
  moduleId: string;
  /** Numbered section (e.g. "9.2") the Word insert targets; null when
   *  the module couldn't be resolved to a skeleton section. */
  sectionNumber: string | null;
  /** Writer-facing label, e.g. "§9.2" or the module title. */
  sectionLabel: string;
  instruction: string;
  content: string;
  wordCount: number;
  baseSectionVersion: number | null;
  /** "draft" = ad-hoc content (instruction overrode the module
   *  instructions); "redraft" = full section redraft via /regen
   *  (writer changes additive to the configured instructions, regen
   *  bookkeeping on approve); "edit" = chat edit-section revision of
   *  the existing content. Drives card labels and server-side
   *  bookkeeping on approve. */
  mode: "draft" | "redraft" | "edit";
}

export type AdhocProposalPhase = "idle" | "pending_decision" | "applying";

/** Result of the shared commit-then-write core. ``null`` (returned directly by
 *  ``commitAndWrite``) means a newer flow superseded this one mid-await. */
type CommitOutcome =
  | { kind: "applied"; note: string }
  | { kind: "conflict"; currentVersion: number }
  | { kind: "error"; message: string };

/** Snapshot handed to ``onDecisionRecorded`` so the shell can append
 *  the durable ``adhoc_draft`` EventLog entry with the outcome baked in. */
export interface AdhocProposalDecisionRecord {
  moduleId: string;
  sectionNumber: string | null;
  instruction: string;
  content: string;
  wordCount: number;
  mode: "draft" | "redraft" | "edit";
}

export interface UseAdhocProposalFlowParams {
  /** Record the decision in the EventLog (shell owns dispatch). */
  onDecisionRecorded: (
    decision: "accepted" | "rejected",
    note: string,
    record: AdhocProposalDecisionRecord,
  ) => void;
  /** Surface a writer-readable error (rendered as an error note). */
  onError: (message: string) => void;
}

export interface UseAdhocProposalFlowResult {
  phase: AdhocProposalPhase;
  /** The active proposal while phase === "pending_decision" | "applying". */
  proposal: AdhocProposal | null;
  /** Set when the last Approve hit version drift — rendered as the
   *  card's warning; the next Approve overwrites deliberately. */
  driftWarning: string | null;
  /** Stepped checklist for the post-approve apply pipeline (save →
   *  Word). Empty unless an APPROVE is in flight — a reject enters
   *  "applying" too but does no Word write, so it shows no checklist. */
  applySteps: ApplyStep[];
  /** Offer a proposal (from SSE or rehydration). Replaces any current
   *  card — latest wins. */
  offer: (proposal: AdhocProposal) => void;
  /** Auto-apply a proposal with NO Approve/Reject card — commit SSOT + write
   *  the section into the live doc immediately (the same pipeline Approve runs).
   *  Used for the chat ``/redraft`` auto-load path. On version drift it falls
   *  back to ``offer`` (the manual card) so a later edit is never silently
   *  overwritten; it also falls back to the card when a decision is already in
   *  flight. */
  autoApply: (proposal: AdhocProposal) => Promise<void>;
  approve: () => Promise<void>;
  reject: () => Promise<void>;
}

export function useAdhocProposalFlow(params: UseAdhocProposalFlowParams): UseAdhocProposalFlowResult {
  const { onDecisionRecorded, onError } = params;
  const [phase, setPhase] = useState<AdhocProposalPhase>("idle");
  const [proposal, setProposal] = useState<AdhocProposal | null>(null);
  const [driftWarning, setDriftWarning] = useState<string | null>(null);
  // Post-approve apply progress. ``applyLabels`` is populated only by the
  // approve path (empty for reject), so the checklist shows only when a
  // Word write is actually happening.
  const [applyLabels, setApplyLabels] = useState<string[]>([]);
  const [applyStepIndex, setApplyStepIndex] = useState(-1);
  // Monotonic token so a stale async result can't resurrect a flow the
  // writer already decided / a newer proposal already replaced.
  const flowTokenRef = useRef(0);
  // After a version_conflict 409 this holds the server's current
  // version — the next Approve submits it, making the overwrite an
  // explicit two-click decision.
  const overrideVersionRef = useRef<number | null>(null);

  const offer = useCallback((next: AdhocProposal) => {
    flowTokenRef.current++;
    overrideVersionRef.current = null;
    setApplyLabels([]);
    setApplyStepIndex(-1);
    setProposal(next);
    setDriftWarning(null);
    setPhase("pending_decision");
  }, []);

  const record = useCallback(
    (p: AdhocProposal): AdhocProposalDecisionRecord => ({
      moduleId: p.moduleId,
      sectionNumber: p.sectionNumber,
      instruction: p.instruction,
      content: p.content,
      wordCount: p.wordCount,
      mode: p.mode,
    }),
    [],
  );

  // Shared commit-then-write core for BOTH the manual Approve path and the
  // ``/redraft`` auto-apply path. Pure of card/phase/label state — the caller
  // owns those; this only touches the version-override ref, the flow token,
  // and the durable SSOT + Word write. Returns a discriminated outcome (or
  // ``null`` when a newer flow superseded this one mid-await) so each caller can
  // render conflicts/errors its own way. ``onBeforeWord`` runs just before the
  // Word insert (the approve path uses it to advance its apply checklist).
  const commitAndWrite = useCallback(
    async (
      p: AdhocProposal,
      token: number,
      onBeforeWord?: () => void,
    ): Promise<CommitOutcome | null> => {
      // Sanitized ONCE — the identical string the card previews, the
      // decision endpoint stores in draft_full, and Word receives.
      const cleaned = sanitizeDraftForWord(p.content);

      // 1. SSOT first.
      const result = await decideAdhocProposal(p.runId, p.moduleId, {
        action: "approve",
        expectedVersion: overrideVersionRef.current ?? p.baseSectionVersion,
        content: cleaned,
      });
      if (flowTokenRef.current !== token) return null;

      if (result.kind === "version_conflict") {
        return { kind: "conflict", currentVersion: result.currentVersion };
      }
      // The proposal record vanished server-side (decided elsewhere,
      // replaced, or wiped by a stale end-of-run archival) — but the
      // drafted content is right here. NEVER discard it: fall back to the
      // direct version-checked edit path. Deliberately not
      // insertProposalIntoSection (it silently retries on conflict, which
      // would defeat the two-click drift protection).
      let viaFallback = false;
      if (result.kind === "no_pending_proposal") {
        try {
          const snap = await getSectionState(p.runId);
          if (flowTokenRef.current !== token) return null;
          const section = snap.sections.find((s) => s.module_id === p.moduleId);
          if (!section) throw new Error("section not found in run state");
          const walked = await walkToInserted(p.runId, p.moduleId, section.status);
          if (flowTokenRef.current !== token) return null;
          const direct = await submitAgentRedraft({
            runId: p.runId,
            moduleId: p.moduleId,
            content: cleaned,
            expectedVersion:
              walked ?? overrideVersionRef.current ?? p.baseSectionVersion ?? section.section_version,
          });
          if (flowTokenRef.current !== token) return null;
          if (direct.conflict) {
            return { kind: "conflict", currentVersion: direct.conflict.currentVersion };
          }
          if (!direct.ok) throw new Error(direct.error ?? "direct edit failed");
          viaFallback = true;
        } catch (err) {
          if (flowTokenRef.current !== token) return null;
          const detail = err instanceof Error ? err.message : String(err);
          return {
            kind: "error",
            message: `Couldn't apply the draft — the proposal record was already cleared server-side and the direct save failed (${detail}).`,
          };
        }
      }
      if (!viaFallback && result.kind !== "approved") {
        const message = result.kind === "error" ? result.message : "unexpected response";
        return { kind: "error", message: `Couldn't apply the draft: ${message}` };
      }

      // 2. Word second — in-place section replace. The SSOT is already
      //    committed; a failure here only means the open doc is stale.
      onBeforeWord?.();
      const sectionLabel = p.sectionNumber ? `§${p.sectionNumber}` : p.sectionLabel;
      const applied = p.mode === "redraft" ? "the redraft" : p.mode === "edit" ? "the edit" : "the new draft";
      let note: string;
      if (p.sectionNumber) {
        let wordOk = false;
        try {
          wordOk = await insertSectionContent({
            sectionNumber: p.sectionNumber,
            content: cleaned,
            scrollAfterInsert: true,
          });
        } catch {
          wordOk = false;
        }
        note = wordOk
          ? `Applied ${applied} to ${sectionLabel}.`
          : `Saved to the record — couldn't update ${sectionLabel} in the document; use Load to refresh.`;
      } else {
        note = `Saved to the record — the section couldn't be located in the document; use Load to refresh.`;
      }
      if (viaFallback) {
        note += " (The proposal record had already been cleared server-side — saved via the direct edit path.)";
      }
      return { kind: "applied", note };
    },
    [],
  );

  const approve = useCallback(async () => {
    if (!proposal || phase !== "pending_decision") return;
    const token = ++flowTokenRef.current;
    // Stage checklist for the approve path: SSOT commit, then Word render.
    setApplyLabels([APPLY_STEP_SAVE, APPLY_STEP_WORD]);
    setApplyStepIndex(0);
    setPhase("applying");

    const outcome = await commitAndWrite(proposal, token, () => setApplyStepIndex(1));
    if (outcome === null) return; // superseded by a newer flow
    if (outcome.kind === "conflict") {
      overrideVersionRef.current = outcome.currentVersion;
      setDriftWarning(
        "This section was edited after the draft was made — approving again will overwrite those changes.",
      );
      setPhase("pending_decision");
      return;
    }
    if (outcome.kind === "error") {
      setPhase("pending_decision");
      onError(`${outcome.message} The draft is still shown above.`);
      return;
    }
    if (flowTokenRef.current !== token) return;
    onDecisionRecorded("accepted", outcome.note, record(proposal));
    setProposal(null);
    setDriftWarning(null);
    setPhase("idle");
  }, [proposal, phase, onDecisionRecorded, onError, record, commitAndWrite]);

  const autoApply = useCallback(
    async (p: AdhocProposal) => {
      // Only auto-apply from a clean idle state — if a decision/card is
      // already in flight, don't clobber it; surface this one as a card.
      if (phase !== "idle") {
        offer(p);
        return;
      }
      const token = ++flowTokenRef.current;
      overrideVersionRef.current = null;
      const outcome = await commitAndWrite(p, token);
      if (outcome === null) return; // superseded
      if (outcome.kind === "conflict") {
        // Version drift — never overwrite silently. Fall back to the manual
        // Approve/Reject card, armed with the fresh version so a deliberate
        // second Approve overwrites (mirrors the approve() drift contract).
        offer(p);
        overrideVersionRef.current = outcome.currentVersion;
        setDriftWarning(
          "This section was edited after the draft was made — approving again will overwrite those changes.",
        );
        return;
      }
      if (outcome.kind === "error") {
        // Couldn't auto-apply — surface the drafted content as a card so the
        // writer can retry via Approve rather than losing the redraft.
        offer(p);
        onError(outcome.message);
        return;
      }
      onDecisionRecorded("accepted", outcome.note, record(p));
    },
    [phase, offer, commitAndWrite, onDecisionRecorded, onError, record],
  );

  const reject = useCallback(async () => {
    if (!proposal || phase !== "pending_decision") return;
    const token = ++flowTokenRef.current;
    // Reject does no Word write — keep the checklist empty so the card
    // shows its plain busy state, not the apply stages.
    setApplyLabels([]);
    setApplyStepIndex(-1);
    setPhase("applying");
    const result = await decideAdhocProposal(proposal.runId, proposal.moduleId, { action: "reject" });
    if (flowTokenRef.current !== token) return;
    const sectionLabel = proposal.sectionNumber ? `§${proposal.sectionNumber}` : proposal.sectionLabel;
    const what = proposal.mode === "redraft" ? "redraft" : proposal.mode === "edit" ? "edit" : "draft";
    const note =
      result.kind === "rejected" || result.kind === "no_pending_proposal"
        ? `Rejected the proposed ${what} for ${sectionLabel}.`
        : `Rejected the proposed ${what} for ${sectionLabel} (the backend record couldn't be updated — it may reappear on reload).`;
    onDecisionRecorded("rejected", note, record(proposal));
    setProposal(null);
    setDriftWarning(null);
    setPhase("idle");
  }, [proposal, phase, onDecisionRecorded, record]);

  // Project stage labels + active index into the checklist; gated on the
  // applying phase so a reverted flow never shows stale steps.
  const applySteps: ApplyStep[] =
    phase === "applying"
      ? applyLabels.map((label, i) => ({
          id: `adhoc-apply-${i}`,
          label,
          state: i < applyStepIndex ? "done" : i === applyStepIndex ? "active" : "pending",
        }))
      : [];

  return { phase, proposal, driftWarning, applySteps, offer, autoApply, approve, reject };
}
